import { Autocomplete, Chip, TextField } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

import {
  ClientKind,
  convertClientKind,
  convertRegistrationUf,
  convertReportFunction,
  RegistrationUf,
  ReportFunction,
  StatusCode,
} from '../../../../api/enumerations';
import { getPipelineFilters } from '../../../../api/workOrders';
import { REPORT_FUNCTION_OPTIONS } from '../../../../constants/reportFunction';
import useErrorMessage from '../../../../hooks/useErrorMessage';
import useSnackbar from '../../../../hooks/useSnackbar';
import { CLIENT_KIND_OPTIONS } from '../../constants';
import { DashboardFilters } from '../../hooks/useDashboardFilters';
import { Wrapper } from './styles';

interface FiltersBarProps {
  filters: DashboardFilters;
  onUf: (uf: RegistrationUf | undefined) => void;
  onCompany: (id: number | undefined) => void;
  onClientKinds: (list: ClientKind[]) => void;
  onReportFunctions: (list: ReportFunction[]) => void;
  onClear: () => void;
}

interface CompanyOption {
  id: number;
  name: string;
}

export function FiltersBar({
  filters,
  onUf,
  onCompany,
  onClientKinds,
  onReportFunctions,
  onClear,
}: FiltersBarProps): JSX.Element {
  const [ufOptions, setUfOptions] = useState<RegistrationUf[]>([]);
  const [companyOptions, setCompanyOptions] = useState<CompanyOption[]>([]);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();

  const loadOptions = useCallback(async () => {
    try {
      const response = await getPipelineFilters({});
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      if (response.detail.status_code !== StatusCode.OK) {
        throw new Error('Algo deu errado, tente novamente.');
      }
      const ufs = response.data.address
        .map((a) => a.uf)
        .filter((uf): uf is RegistrationUf => !!uf);
      setUfOptions(ufs);
      if (response.data.worker_companies) {
        setCompanyOptions(
          response.data.worker_companies.map((c) => ({
            id: c.id,
            name: c.name,
          }))
        );
      }
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadOptions();
  }, [loadOptions]);

  const selectedCompany =
    companyOptions.find((c) => c.id === filters.companyId) || null;

  const hasAnyExtra =
    !!filters.uf ||
    !!filters.companyId ||
    filters.clientKinds.length > 0 ||
    filters.reportFunctions.length > 0;

  return (
    <Wrapper>
      <Autocomplete
        size="small"
        sx={{ minWidth: 140 }}
        options={ufOptions}
        value={filters.uf || null}
        onChange={(_, v) => onUf(v ?? undefined)}
        getOptionLabel={(opt) => convertRegistrationUf(opt)}
        renderInput={(params) => <TextField {...params} label="UF" />}
      />
      <Autocomplete
        size="small"
        sx={{ minWidth: 220 }}
        options={companyOptions}
        value={selectedCompany}
        onChange={(_, v) => onCompany(v?.id)}
        getOptionLabel={(opt) => opt.name}
        renderInput={(params) => <TextField {...params} label="empresa" />}
      />
      <Autocomplete
        multiple
        size="small"
        sx={{ minWidth: 240 }}
        options={CLIENT_KIND_OPTIONS}
        value={filters.clientKinds}
        onChange={(_, v) => onClientKinds(v as ClientKind[])}
        getOptionLabel={(opt) => convertClientKind(opt)}
        renderInput={(params) => <TextField {...params} label="tipo cliente" />}
      />
      <Autocomplete
        multiple
        size="small"
        sx={{ minWidth: 240 }}
        options={REPORT_FUNCTION_OPTIONS}
        value={filters.reportFunctions}
        onChange={(_, v) => onReportFunctions(v as ReportFunction[])}
        getOptionLabel={(opt) => convertReportFunction(opt)}
        renderInput={(params) => <TextField {...params} label="finalidade" />}
      />
      {hasAnyExtra && (
        <Chip
          label="limpar filtros"
          variant="outlined"
          size="small"
          onClick={onClear}
        />
      )}
    </Wrapper>
  );
}
